/**
 * Character Counter Controller
 * 
 * Reusable Stimulus controller for counting characters in textareas
 * and displaying the count with optional max limit.
 * 
 * Usage:
 * <div data-controller="character-counter"
 *      data-character-counter-max-value="10000">
 *   <textarea data-character-counter-target="input"
 *             data-action="input->character-counter#update"></textarea>
 *   <span data-character-counter-target="count">0</span> / 10,000 characters
 * </div>
 * 
 * Targets:
 * - input: The textarea to track
 * - count: Element to display current character count
 * - warning: (optional) Element to show when approaching limit
 * 
 * Values:
 * - max: Maximum character limit (default: 10000)
 * - warningThreshold: Percentage threshold for warning (default: 0.9 = 90%)
 */

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "count", "warning"]
  static values = {
    max: { type: Number, default: 10000 },
    warningThreshold: { type: Number, default: 0.9 }
  }

  connect() {
    // Initialize count on connect
    this.update()
  }

  update() {
    if (!this.hasInputTarget || !this.hasCountTarget) return

    const currentLength = this.inputTarget.value.length
    this.countTarget.textContent = currentLength

    // Update visual feedback based on character count
    this.updateFeedback(currentLength)
  }

  updateFeedback(length) {
    const thresholdAmount = Math.floor(this.maxValue * this.warningThresholdValue)
    
    // Remove all state classes first
    this.countTarget.classList.remove("text-warning", "text-danger", "text-muted")
    
    if (length >= this.maxValue) {
      // Over limit
      this.countTarget.classList.add("text-danger")
      if (this.hasWarningTarget) {
        this.warningTarget.classList.remove("d-none")
        this.warningTarget.textContent = "Character limit reached"
      }
    } else if (length >= thresholdAmount) {
      // Approaching limit
      this.countTarget.classList.add("text-warning")
      if (this.hasWarningTarget) {
        this.warningTarget.classList.remove("d-none")
        this.warningTarget.textContent = `${this.maxValue - length} characters remaining`
      }
    } else {
      // Normal state
      this.countTarget.classList.add("text-muted")
      if (this.hasWarningTarget) {
        this.warningTarget.classList.add("d-none")
      }
    }
  }

  // Reset the counter (useful after form submission)
  reset() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    this.update()
  }
}